import { CheckboxControl, InputControl } from "@shared/components/form";
import { MediaUploader } from "@shared/components/media/MediaUploader";
import ModalBlank from "@shared/components/modals/ModalBlank";
import { Button } from "@shared/components/ui/Button";
import { Trash2 } from "lucide-react";
import React, { useId, useState } from "react";
import { toast } from "react-hot-toast";
import { LeaderboardSectionData, LeaderboardTheme } from "../../types/home.types";
import LeaderboardDataPanel, { DraftEntry, MAX_ENTRIES, toDraft } from "./LeaderboardDataPanel";
import LeaderboardSection from "./LeaderboardSection";
import {
  DEFAULT_THEME,
  rankEntries,
  THEME_FIELDS,
  THEME_PRESETS,
} from "./leaderboard.utils";

interface EditLeaderboardSectionModalProps {
  initialData?: LeaderboardSectionData;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<LeaderboardSectionData>) => Promise<void>;
}

type Tab = "data" | "display" | "theme";

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "data", label: "Data" },
  { id: "display", label: "Display" },
  { id: "theme", label: "Theme" },
];

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** `<input type="color">` only understands #rrggbb. */
const toSixDigitHex = (hex: string) =>
  hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;

const EditLeaderboardSectionModal: React.FC<EditLeaderboardSectionModalProps> = ({
  initialData,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const modalId = useId();
  const [tab, setTab] = useState<Tab>("data");
  const [isSaving, setIsSaving] = useState(false);

  const [enabled, setEnabled] = useState(initialData?.enabled ?? false);
  const [title, setTitle] = useState(initialData?.title ?? "The Wait Is Over");
  const [subtitle, setSubtitle] = useState(initialData?.subtitle ?? "Leaderboard");
  const [logoUrl, setLogoUrl] = useState(initialData?.logoUrl ?? "");
  const [showPoints, setShowPoints] = useState(initialData?.showPoints ?? true);
  const [showEmail, setShowEmail] = useState(initialData?.showEmail ?? false);
  const [topN, setTopN] = useState(String(initialData?.topN ?? 10));
  const [themePreset, setThemePreset] = useState(initialData?.themePreset ?? "biomarker");
  const [theme, setTheme] = useState<LeaderboardTheme>({
    ...DEFAULT_THEME,
    ...initialData?.theme,
  });

  const [entries, setEntries] = useState<DraftEntry[]>(() => (initialData?.entries ?? []).map(toDraft));
  const [sourceFileName, setSourceFileName] = useState(initialData?.sourceFileName ?? "");
  const [entriesChanged, setEntriesChanged] = useState(false);

  const topNValue = Math.min(100, Math.max(3, Math.round(Number(topN)) || 10));

  const handleEntriesChange = (next: DraftEntry[], fileName?: string) => {
    setEntries(next);
    setEntriesChanged(true);
    if (fileName !== undefined) setSourceFileName(fileName);
  };

  const handlePreset = (presetId: string) => {
    const preset = THEME_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setThemePreset(preset.id);
    setTheme(preset.theme);
  };

  const handleColor = (key: keyof LeaderboardTheme, value: string) => {
    setTheme((current) => ({ ...current, [key]: value }));
    setThemePreset("custom");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const invalidColor = THEME_FIELDS.find(({ key }) => !HEX_COLOR.test(theme[key]));
    if (invalidColor) {
      setTab("theme");
      toast.error(`"${invalidColor.label}" is not a valid color (use #RRGGBB).`);
      return;
    }

    const payload: Partial<LeaderboardSectionData> = {
      enabled,
      title: title.trim(),
      subtitle: subtitle.trim(),
      logoUrl: logoUrl.trim(),
      showPoints,
      showEmail,
      topN: topNValue,
      themePreset,
      theme,
    };

    if (entriesChanged) {
      payload.entries = rankEntries(entries)
        .slice(0, MAX_ENTRIES)
        .map(({ name, email, points }) => (email ? { name, email, points } : { name, points }));
      payload.sourceFileName = sourceFileName;
    }

    setIsSaving(true);
    try {
      await onSubmit(payload);
      toast.success("Leaderboard updated");
    } catch (error) {
      console.error("Error updating leaderboard section:", error);
      toast.error("Failed to update the leaderboard");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalBlank
      id={modalId}
      modalOpen={isOpen}
      setModalOpen={() => onClose()}
      showCloseButton
      className="!max-w-5xl"
    >
      <div className="p-5 sm:p-6">
        <div className="mb-5 text-center">
          <div className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            Edit Leaderboard
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Upload a spreadsheet with names and points and pick how it looks
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="overflow-y-auto max-h-[calc(100vh-260px)] pr-1">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-5">
              <CheckboxControl
                id="leaderboard-enabled"
                label="Show the leaderboard on the home page"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={isSaving}
                description="It takes the spot next to the video, and the recordings move to a full-width row below. Turn it off to put the recordings back in that spot."
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-6">
              <div className="min-w-0">
                <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4">
                  {TABS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTab(t.id)}
                      className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
                        tab === t.id
                          ? "border-violet-500 text-violet-600 dark:text-violet-400"
                          : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {tab === "data" && (
                  <LeaderboardDataPanel
                    entries={entries}
                    onEntriesChange={handleEntriesChange}
                    sourceFileName={sourceFileName}
                    hasUnsavedChanges={entriesChanged}
                    topN={topNValue}
                  />
                )}

                {tab === "display" && (
                  <div className="space-y-4">
                    <InputControl
                      id="leaderboard-title"
                      label="Title"
                      value={title}
                      maxLength={120}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                    <InputControl
                      id="leaderboard-subtitle"
                      label="Subtitle"
                      value={subtitle}
                      maxLength={120}
                      onChange={(e) => setSubtitle(e.target.value)}
                    />
                    <InputControl
                      id="leaderboard-top-n"
                      label="People shown (podium included)"
                      type="number"
                      min="3"
                      max="100"
                      value={topN}
                      onChange={(e) => setTopN(e.target.value)}
                      error={topN !== "" && String(topNValue) !== topN ? `It will be saved as ${topNValue} (3–100).` : null}
                    />
                    <CheckboxControl
                      id="leaderboard-show-points"
                      label="Show points"
                      checked={showPoints}
                      onChange={(e) => setShowPoints(e.target.checked)}
                      description="Turn off to show only the ranking."
                    />
                    <CheckboxControl
                      id="leaderboard-show-email"
                      label="Show email"
                      checked={showEmail}
                      onChange={(e) => setShowEmail(e.target.checked)}
                      description="Every member who opens the home page will see the addresses, under each name."
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Logo (optional)
                      </label>
                      {logoUrl && (
                        <div className="flex items-center gap-3 mb-2">
                          <div className="rounded p-2" style={{ background: theme.background }}>
                            <img src={logoUrl} alt="" className="h-10 w-auto object-contain" />
                          </div>
                          <Button type="button" size="sm" variant="ghost" onClick={() => setLogoUrl("")}>
                            <Trash2 className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        </div>
                      )}
                      <MediaUploader
                        onUploadComplete={(files) => files[0]?.uploadedUrl && setLogoUrl(files[0].uploadedUrl)}
                        onError={(error) => toast.error(error)}
                        maxFiles={1}
                        acceptType="image"
                      />
                    </div>
                  </div>
                )}

                {tab === "theme" && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {THEME_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => handlePreset(preset.id)}
                          className={`rounded-lg border-2 p-2 text-left transition-colors ${
                            themePreset === preset.id
                              ? "border-violet-500"
                              : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                          }`}
                        >
                          <div
                            className="h-10 rounded flex items-center justify-center gap-1 mb-1.5"
                            style={{ background: preset.theme.background }}
                          >
                            {[
                              [preset.theme.podiumSecondFrom, preset.theme.podiumSecondTo],
                              [preset.theme.podiumFirstFrom, preset.theme.podiumFirstTo],
                              [preset.theme.podiumThirdFrom, preset.theme.podiumThirdTo],
                            ].map(([from, to], i) => (
                              <span
                                key={i}
                                className="rounded-full"
                                style={{
                                  width: i === 1 ? 18 : 14,
                                  height: i === 1 ? 18 : 14,
                                  background: `linear-gradient(135deg, ${from}, ${to})`,
                                }}
                              />
                            ))}
                          </div>
                          <div className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                            {preset.label}
                          </div>
                        </button>
                      ))}
                    </div>
                    {themePreset === "custom" && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
                        Custom colors. Pick a theme above to start over.
                      </p>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                      {THEME_FIELDS.map(({ key, label }) => (
                        <div key={key} className="flex items-center gap-2">
                          <input
                            type="color"
                            aria-label={label}
                            value={HEX_COLOR.test(theme[key]) ? toSixDigitHex(theme[key]) : "#000000"}
                            onChange={(e) => handleColor(key, e.target.value.toUpperCase())}
                            className="h-9 w-10 shrink-0 cursor-pointer rounded border border-gray-200 dark:border-gray-700 bg-transparent p-0.5"
                          />
                          <div className="min-w-0 flex-1">
                            <label
                              htmlFor={`leaderboard-color-${key}`}
                              className="block text-xs text-gray-500 dark:text-gray-400 truncate"
                            >
                              {label}
                            </label>
                            <input
                              id={`leaderboard-color-${key}`}
                              value={theme[key]}
                              maxLength={7}
                              onChange={(e) => handleColor(key, e.target.value.trim())}
                              className={`form-input w-full py-1 text-sm font-mono ${
                                HEX_COLOR.test(theme[key]) ? "" : "border-red-500"
                              }`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Preview
                </div>
                <div className="lg:sticky lg:top-0">
                  <LeaderboardSection
                    compact
                    title={title}
                    subtitle={subtitle}
                    logoUrl={logoUrl}
                    showPoints={showPoints}
                    showEmail={showEmail}
                    topN={topNValue}
                    theme={theme}
                    entries={entries}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSaving} loadingText="Saving...">
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </ModalBlank>
  );
};

export default EditLeaderboardSectionModal;
